<template>
  <!-- NETFLIX THEME LAYOUT -->
  <div v-if="theme?.theme?.type === 'built-in' && theme?.theme?.style === 'netflix' && isRootPath" class="netflix-layout">
    <!-- NETFLIX STYLE TEMPLATE START -->
    <main
      ref="mainNetflixElement"
      class="flex-grow"
    >
      <div class="w-full">
        <NuxtPage />
      </div>
    </main>
    <!-- Live Chat Support -->
    <LiveChatSupport v-if="isLocalhost" />
    <!-- NETFLIX STYLE TEMPLATE END -->
  </div>
  
  <!-- CLUSTERSIE THEME LAYOUT FOR ROOT -->
  <div v-else-if="theme?.theme?.type === 'clustersie' && isRootPath" class="clustersie-layout">
    <!-- CLUSTERSIE STYLE TEMPLATE START -->
    <main
      ref="mainClustersieElement"
      class="flex-grow"
    >
      <div class="w-full">
        <NuxtPage />
      </div>
    </main>
    <!-- Live Chat Support -->
    <LiveChatSupport v-if="isLocalhost" />
    <!-- CLUSTERSIE STYLE TEMPLATE END -->
  </div>
  
  <!-- CLUSTER PAGES LAYOUT -->
  <div v-else-if="isClusterPath" class="clustersie-layout">
    <!-- CLUSTER STYLE TEMPLATE START -->
    <ClusterHeader 
      :currentSite="theme?.theme?.subdomain" 
      :mode="'pages'"
      :activeSection="'cluster'"
    />
    <main
      ref="mainClusterElement"
      class="flex-grow"
    >
      <div class="w-full">
        <NuxtPage />
      </div>
    </main>
    <ClusterFooter 
    :mode="'pages'"/>

    <!-- Live Chat Support -->
    <LiveChatSupport v-if="isLocalhost" />
    <!-- CLUSTER STYLE TEMPLATE END -->
  </div>
  
  <div v-else class="relative flex flex-col min-h-screen">
    <Header v-if="!$isLiff"/>
    <main
      ref="mainElement"
      :class="['flex-grow flex justify-center items-start', backgroundClass]"
    >
      <div class="w-full">
        <NuxtPage />
      </div>
    </main>
    <Footer v-if="!$isLiff"/>
    <!-- Live Chat Support -->
    <LiveChatSupport v-if="!$isLiff && isLocalhost" />
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, computed, provide, inject, defineAsyncComponent } from 'vue';
import { setContentHeight } from '~/composables/useContentHeight';
// Lazy load components for better performance
const Header = defineAsyncComponent(() => import('~/components/template/Head.vue'));
const Footer = defineAsyncComponent(() => import('~/components/template/Foot.vue'));
const LiveChatSupport = defineAsyncComponent(() => import('~/components/livesupport/index.vue'));
const ClusterHeader = defineAsyncComponent(() => import('~/layouts/theme/custom/clustersie/components/SiteHeader.vue'));
const ClusterFooter = defineAsyncComponent(() => import('~/layouts/theme/custom/clustersie/components/SiteFooter.vue'));
import eventBus from '~/eventBus';
import { useRender } from '~/composables/useRender';

import { useNuxtApp, useRoute } from '#app'; // For accessing the userAgent from the plugin


const theme = inject('configs');
//console.log('theme', theme);

// Get the userAgent from the plugin
const { $userAgent, $isLiff } = useNuxtApp();

const route = useRoute(); // Add this line

const { updateStatus } = useRender();
const background = ref(null);

function handleBackgroundUpdate(value) {
  background.value = value;
}

const mainElement = ref(null);
const mainNetflixElement = ref(null); // Ref for Netflix theme
const mainClustersieElement = ref(null); // Ref for Clustersie theme
const mainClusterElement = ref(null); // Ref for Cluster pages

onMounted(() => {
  // Register the event listener early
  eventBus.on('updateBackground', handleBackgroundUpdate);

  // Observe the height of the main element
  const updateMainElementHeight = () => {
    if (mainElement.value) {
      setContentHeight(mainElement.value.offsetHeight);
    }
    // Handle Netflix theme
    if (mainNetflixElement.value) {
      setContentHeight(mainNetflixElement.value.offsetHeight);
    }
    // Handle Clustersie theme
    if (mainClustersieElement.value) {
      setContentHeight(mainClustersieElement.value.offsetHeight);
    }
    // Handle Cluster theme
    if (mainClusterElement.value) {
      setContentHeight(mainClusterElement.value.offsetHeight);
    }
  };

  updateMainElementHeight(); // Initial update
  const resizeObserver = new ResizeObserver(() => updateMainElementHeight());
  if (mainElement.value) resizeObserver.observe(mainElement.value);
  if (mainNetflixElement.value) resizeObserver.observe(mainNetflixElement.value); // Observe Netflix theme
  if (mainClustersieElement.value) resizeObserver.observe(mainClustersieElement.value); // Observe Clustersie theme
  if (mainClusterElement.value) resizeObserver.observe(mainClusterElement.value); // Observe Cluster theme

  updateStatus(true);

  console.log('User-Agent:', $userAgent); // Logs the user agent
  console.log('Is LIFF:', $isLiff); // Logs true if 'LIFF' is in the user agent
  console.log('Is Root Path:', route.path === '/');
  console.log('Theme Type:', theme?.theme?.type); // Logs current theme type
  console.log('Theme Style:', theme?.theme?.style); // Logs current theme style

  // Cleanup listener and observer
  onUnmounted(() => {
    eventBus.off('updateBackground', handleBackgroundUpdate);
    if (resizeObserver) resizeObserver.disconnect(); // Ensure observer is disconnected
  });
});

// Provide the debug state
const showDebug = ref(false);
provide('showDebug', showDebug);

// Compute the background class dynamically
const backgroundClass = computed(() => (background.value ? `${background.value}` : 'bg-white'));

// Computed property to check if the current path is the root path
const isRootPath = computed(() => route.path === '/');

// Computed property to check if current path is cluster path
const isClusterPath = computed(() => route.path.startsWith('/cluster'));

// Computed property to check if running on localhost
const isLocalhost = computed(() => {
  if (process.client) {
    return window.location.hostname === 'frontend.dev.test' || window.location.hostname === '127.0.0.1';
  }
  return false;
});
</script>



<style>
body {
  touch-action: manipulation;
}
.toggle-debug-btn {
  position: fixed;
  bottom: 20px;
  right: 70px;
  padding: 5px 10px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  z-index: 999;
}

/* Netflix theme layout */
.netflix-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Clustersie theme layout */
.clustersie-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
</style>
